import { useMemo } from "react";
import {
  rcToSquare,
  squareToRC,
  type Board as BoardState,
  type Move,
  type VariantSpec,
} from "@fmjd/engine";

/**
 * The board.
 *
 * Draws from the VariantSpec, so 8x8 and 10x10 render with the same code and
 * a 12x12 variant would need nothing here.
 *
 * FMJD convention: square 1 is top-left from White's point of view, White
 * moves up the board. When `flipped` is set the board is drawn from Black's
 * side, which is what a black player must see.
 */

interface BoardProps {
  variant: VariantSpec;
  board: BoardState;
  flipped?: boolean;
  /** Squares to highlight as selectable or as move targets. */
  selected?: number | null;
  targets?: number[];
  /** Squares already entered in a capture being built, including the origin. */
  pathSquares?: number[];
  lastMove?: Move | null;
  onSquareClick?: (square: number) => void;
  /** Show FMJD square numbers — useful for demos and arbiters. */
  showNumbers?: boolean;
  size?: number;
}

export function Board({
  variant,
  board,
  flipped = false,
  selected = null,
  targets = [],
  pathSquares = [],
  lastMove = null,
  onSquareClick,
  showNumbers = false,
  size = 560,
}: BoardProps) {
  const n = variant.boardSize;
  const cell = size / n;
  const targetSet = useMemo(() => new Set(targets), [targets]);
  const pathSet = useMemo(() => new Set(pathSquares), [pathSquares]);
  const lastMoveSquares = useMemo(() => {
    if (!lastMove) return new Set<number>();
    return new Set<number>([lastMove.from, ...lastMove.path]);
  }, [lastMove]);

  const cells = [];
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      // Flipping is a display concern only: the square number is unchanged,
      // so every handler and highlight keeps working.
      const dr = flipped ? n - 1 - row : row;
      const dc = flipped ? n - 1 - col : col;
      const square = rcToSquare(variant, dr, dc);
      const dark = square !== null;
      const piece = square !== null ? board[square - 1] : null;

      const isSelected = square !== null && square === selected;
      const isTarget = square !== null && targetSet.has(square);
      const inPath = square !== null && pathSet.has(square);
      const inLastMove = square !== null && lastMoveSquares.has(square);

      cells.push(
        <div
          key={`${row}-${col}`}
          onClick={dark && square !== null ? () => onSquareClick?.(square) : undefined}
          style={{
            position: "absolute",
            left: col * cell,
            top: row * cell,
            width: cell,
            height: cell,
            background: dark
              ? inPath
                ? "#a3843f"
                : inLastMove
                  ? "#b8905f"
                  : "#8b6b47"
              : "#f0d9b5",
            boxShadow: isSelected ? "inset 0 0 0 3px #2e7d32" : undefined,
            cursor: dark && onSquareClick ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            userSelect: "none",
          }}
        >
          {showNumbers && square !== null && (
            <span
              style={{
                position: "absolute",
                left: 2,
                top: 1,
                fontSize: Math.max(8, cell * 0.18),
                color: "rgba(255,255,255,0.45)",
                pointerEvents: "none",
              }}
            >
              {square}
            </span>
          )}

          {piece && (
            <div
              style={{
                width: cell * 0.78,
                height: cell * 0.78,
                borderRadius: "50%",
                // The highlight follows the board: when it is flipped, the
                // light source has to flip too, or every piece is lit from the
                // wrong side and the whole board looks wrong.
                background:
                  piece.color === "w"
                    ? `radial-gradient(circle at ${flipped ? "65% 70%" : "35% 30%"},
                         #fdfcfa 0%, #efe9df 45%, #cfc6b8 100%)`
                    : `radial-gradient(circle at ${flipped ? "65% 70%" : "35% 30%"},
                         #6a6a6a 0%, #3a3a3a 45%, #141414 100%)`,
                // A darker rim separates a white man from the light squares,
                // which are close to it in value.
                border: `1px solid ${piece.color === "w" ? "#8d8375" : "#000"}`,
                boxShadow:
                  piece.color === "w"
                    ? "0 2px 3px rgba(0,0,0,0.4), inset 0 -2px 3px rgba(0,0,0,0.12)"
                    : "0 2px 3px rgba(0,0,0,0.5), inset 0 -2px 3px rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {piece.king && (
                // Kings need to be unmistakable at a glance on a crowded
                // spectator grid, so they get a filled ring, not a letter.
                <div
                  style={{
                    width: cell * 0.34,
                    height: cell * 0.34,
                    borderRadius: "50%",
                    border: `3px solid ${piece.color === "w" ? "#b8860b" : "#d4af37"}`,
                  }}
                />
              )}
            </div>
          )}

          {isTarget && !piece && (
            <div
              style={{
                width: cell * 0.28,
                height: cell * 0.28,
                borderRadius: "50%",
                background: "rgba(46,125,50,0.55)",
                pointerEvents: "none",
              }}
            />
          )}
          {isTarget && piece && (
            <div
              style={{
                position: "absolute",
                inset: 3,
                borderRadius: "50%",
                border: "3px solid rgba(198,40,40,0.75)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      );
    }
  }

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        border: "3px solid #5d4632",
        borderRadius: 4,
        maxWidth: "100%",
      }}
    >
      {cells}
    </div>
  );
}

/** Numeric FMJD notation for a move, e.g. "32-28" or "28x19". */
export function moveNotation(move: Move): string {
  return move.captures.length > 0
    ? `${move.from}x${move.to}`
    : `${move.from}-${move.to}`;
}

export { squareToRC };
